import {
  GAME_ACTIONS,
  CAMERA_ACTIONS,
  ACTION_LABELS,
  getBindings,
  setKeyboardBinding,
  setGamepadBinding,
  resetKeyboardBindings,
  resetGamepadBindings,
  formatKeyLabel,
  formatGpLabel,
  type Action,
} from "../input/Bindings.js";

export class BindingsPanel {
  private listening: { action: Action; device: "keyboard" | "gamepad" } | null = null;
  private keyListener: ((e: KeyboardEvent) => void) | null = null;
  private mouseListener: ((e: MouseEvent) => void) | null = null;
  private gpScanRaf = 0;
  private gpPrevSnapshot: boolean[] = [];

  constructor(private container: HTMLElement) {
    window.addEventListener("keydown", this.onGlobalEsc);
    this.render();
  }

  destroy(): void {
    window.removeEventListener("keydown", this.onGlobalEsc);
    this.cancelListen();
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  private render(): void {
    this.container.innerHTML = `
      <div class="bindings-col">
        <div class="bindings-col-title">⌨ KEYBOARD + MOUSE</div>
        ${this.renderDevice("keyboard")}
        <button class="bp-reset-btn" data-device="keyboard">RESET KEYBOARD</button>
      </div>
      <div class="bindings-col">
        <div class="bindings-col-title">🎮 CONTROLLER</div>
        ${this.renderDevice("gamepad")}
        <button class="bp-reset-btn" data-device="gamepad">RESET CONTROLLER</button>
      </div>
    `;
    this.attachHandlers();
  }

  private renderDevice(device: "keyboard" | "gamepad"): string {
    const parts = [
      `<div class="bp-group-header">DRIVING</div>`,
      this.renderActionGroup(GAME_ACTIONS, device),
      `<div class="bp-group-header">CAMERA</div>`,
      this.renderActionGroup(CAMERA_ACTIONS, device),
    ];
    if (device === "keyboard") {
      parts.push(
        `<div class="bp-group-header">SHORTCUTS</div>`,
        FIXED_SHORTCUTS.map(([label, key]) =>
          `<div class="bp-row bp-row-fixed"><span class="bp-action">${label}</span><span class="bp-key">${key}</span></div>`,
        ).join(""),
      );
    }
    return parts.join("");
  }

  private renderActionGroup(actions: Action[], device: "keyboard" | "gamepad"): string {
    const bindings = getBindings();
    const map = device === "keyboard" ? bindings.keyboard : bindings.gamepad;
    return actions.map((action) => {
      const isListening = this.listening?.action === action && this.listening?.device === device;
      const raw = map[action] ?? "";
      const label = device === "keyboard" ? formatKeyLabel(raw) : formatGpLabel(raw);
      const cls = ["bp-row", isListening ? "bp-listening" : ""].filter(Boolean).join(" ");
      const keyHtml = isListening
        ? `<span class="bp-key bp-key-waiting">PRESS…</span>`
        : `<span class="bp-key">${escHtml(label)}</span>`;
      return `<div class="${cls}" data-action="${action}" data-device="${device}">
        <span class="bp-action">${ACTION_LABELS[action]}</span>${keyHtml}</div>`;
    }).join("");
  }

  private attachHandlers(): void {
    this.container.querySelectorAll<HTMLElement>(".bp-row").forEach((row) => {
      row.addEventListener("click", () => {
        if (row.classList.contains("bp-listening")) { this.cancelListen(); return; }
        this.startListen(row.dataset.action as Action, row.dataset.device as "keyboard" | "gamepad");
      });
    });

    this.container.querySelectorAll<HTMLButtonElement>(".bp-reset-btn").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (btn.dataset.device === "keyboard") resetKeyboardBindings();
        else resetGamepadBindings();
        this.cancelListen();
        this.render();
      });
    });
  }

  // ─── Listen mode ──────────────────────────────────────────────────────────

  private startListen(action: Action, device: "keyboard" | "gamepad"): void {
    this.cancelListen();
    this.listening = { action, device };
    this.render();

    if (device === "keyboard") {
      this.keyListener = (e: KeyboardEvent) => {
        e.preventDefault();
        if (e.code === "Escape") { this.cancelListen(); return; }
        setKeyboardBinding(action, e.code);
        this.finishListen();
      };
      window.addEventListener("keydown", this.keyListener, { once: true });

      this.mouseListener = (e: MouseEvent) => {
        if (e.button === 0) {
          setKeyboardBinding(action, "MouseLeft");
          this.finishListen();
        }
      };
      window.addEventListener("mousedown", this.mouseListener, { once: true });
    } else {
      // Prime snapshot on first frame to avoid treating held buttons as rising edge
      const pads = navigator?.getGamepads?.() ?? [];
      for (const pad of pads) {
        if (pad?.connected) {
          this.gpPrevSnapshot = pad.buttons.map((b) => b.pressed);
          break;
        }
      }
      this.gpScanRaf = requestAnimationFrame(this.gpScan);
    }
  }

  private gpScan = (): void => {
    if (!this.listening || this.listening.device !== "gamepad") return;
    const action = this.listening.action;

    const pads = navigator?.getGamepads?.() ?? [];
    for (const pad of pads) {
      if (!pad?.connected) continue;

      // Rising-edge button detection (skip btn9 = Start/Menu)
      for (let i = 0; i < pad.buttons.length; i++) {
        if (i === 9) continue;
        if (pad.buttons[i].pressed && !(this.gpPrevSnapshot[i] ?? false)) {
          setGamepadBinding(action, `btn${i}`);
          this.gpPrevSnapshot = [];
          this.finishListen();
          return;
        }
      }

      // Axis detection (threshold 0.6 to avoid drift noise)
      for (let i = 0; i < pad.axes.length; i++) {
        const v = pad.axes[i] ?? 0;
        if (v > 0.6)  { setGamepadBinding(action, `axis${i}+`); this.finishListen(); return; }
        if (v < -0.6) { setGamepadBinding(action, `axis${i}-`); this.finishListen(); return; }
      }

      this.gpPrevSnapshot = pad.buttons.map((b) => b.pressed);
      break;
    }

    this.gpScanRaf = requestAnimationFrame(this.gpScan);
  };

  private finishListen(): void {
    this.cleanupListeners();
    this.listening = null;
    this.render();
  }

  private cancelListen(): void {
    this.cleanupListeners();
    if (this.listening) {
      this.listening = null;
      this.render();
    }
  }

  private cleanupListeners(): void {
    if (this.keyListener) {
      window.removeEventListener("keydown", this.keyListener);
      this.keyListener = null;
    }
    if (this.mouseListener) {
      window.removeEventListener("mousedown", this.mouseListener);
      this.mouseListener = null;
    }
    cancelAnimationFrame(this.gpScanRaf);
    this.gpPrevSnapshot = [];
  }

  private onGlobalEsc = (e: KeyboardEvent): void => {
    if (e.code === "Escape" && this.listening) this.cancelListen();
  };
}

const FIXED_SHORTCUTS: [string, string][] = [
  ["CYCLE VISUALS",    "N"],
  ["REARVIEW MIRROR",  "M"],
  ["SPECTATE NEXT",    "TAB"],
  ["PAUSE / MENU",     "ESC"],
];

function escHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}
